import { createTracker } from '@offlegacy/event-tracker';
import { z } from 'zod';
import { eventMapping } from './event-mapper';

// Type definitions
export interface TrackingContext {
  userId?: string;
  sessionId?: string;
  userRole?: 'customer' | 'cleaner';
  isPremium?: boolean;
  hasConsent?: boolean;
  abTests?: Record<string, string>;
}

// Standardized custom event names type
// These are the internal event names that will be mapped to platform-specific events
export type EventName =
  // User journey events
  | 'CompleteRegistration'
  | 'PageView'

  // User engagement events
  | 'Search'
  | 'FilterResults'
  | 'ViewSearchResults'
  | 'ClickService'
  | 'ViewProvider'
  | 'BookAppointment'
  | 'CancelAppointment'
  | 'RescheduleAppointment'

  // Authentication events
  | 'Login'
  | 'Logout'
  | 'SignUp'
  | 'PasswordReset'
  | 'EmailVerification'

  // Onboarding events
  | 'StartOnboarding'
  | 'CompleteOnboarding'
  | 'SkipOnboarding'

  // Content events
  | 'ViewFAQ'
  | 'ContactSupport'
  | 'ViewTestimonials'
  | 'ViewPricing'
  | 'ViewAbout'

  // Location events
  | 'AllowLocation'
  | 'DenyLocation'
  | 'SearchByLocation'
  | 'ChangeServiceArea'

  // Notification events
  | 'EnableNotifications'
  | 'DisableNotifications'
  | 'ReceiveNotification'
  | 'ClickNotification'

  // Referral events
  | 'ShareReferral'
  | 'AcceptReferral'

  // Feedback events
  | 'SubmitRating'
  | 'SubmitReview'
  | 'ReportIssue'

  // Application events (for cleaners)
  | 'StartApplication'
  | 'SubmitApplication'
  | 'CompleteBackgroundCheck'
  | 'ApplicationApproved'
  | 'ApplicationRejected';

// Event parameters type
export type EventParams = {
  event: EventName;
  [key: string]: unknown;
};

// Check if tracking is enabled
function isTrackingEnabled(): boolean {
  const isProd = import.meta.env.MODE === 'production';
  return isProd || import.meta.env.VITE_ENABLE_TRACKER === 'true';
}

// Create tracker instance
export const [Track, useTracker] = createTracker<TrackingContext, EventParams>({
  DOMEvents: {
    onClick: (params, context) => {
      if (!isTrackingEnabled()) return params;

      // Meta Pixel is already initialized in index.html

      // Get mapped event names
      const metaPixelEvent = eventMapping.mapToMetaPixel(params.event);
      const ga4Event = eventMapping.mapToGA4(params.event);

      // Enrich event data
      const enrichedEvent = {
        ...params,
        ...context,
        timestamp: Date.now(),
        url: window.location.href,
      };

      // Forward to Meta Pixel (only if fbq exists and pixel was initialized in index.html)
      if (window.fbq && window.fbqPixelInitialized && metaPixelEvent) {
        const metaPixelParams = eventMapping.getMetaPixelParams(
          metaPixelEvent,
          enrichedEvent
        );
        const isStandardEvent = [
          'PageView',
          'Lead',
          'CompleteRegistration',
          'InitiateCheckout',
          'Purchase',
          'ViewContent',
          'Search',
          'AddToCart',
          'AddToWishlist',
          'Schedule',
          'StartTrial',
          'SubmitApplication',
          'Contact',
          'Donate',
          'FindLocation',
          'CustomizeProduct',
        ].includes(metaPixelEvent);

        if (isStandardEvent) {
          window.fbq('track', metaPixelEvent, metaPixelParams);
        } else {
          window.fbq('trackCustom', metaPixelEvent, metaPixelParams);
        }
      }

      // Forward to GA4
      if (window.gtag && ga4Event) {
        const ga4Params = eventMapping.getGA4Params(ga4Event, enrichedEvent);
        window.gtag('event', ga4Event, ga4Params);
      }

      // Forward to GTM dataLayer
      if (window.dataLayer) {
        window.dataLayer.push({
          event_name: ga4Event,
          ...enrichedEvent,
        });
      }

      return enrichedEvent;
    },
  },

  pageView: {
    onPageView: (params, _context) => {
      if (!isTrackingEnabled()) return;

      // Meta Pixel is already initialized in index.html

      // GA4 PageView (gtag.js handles this automatically via config in index.html)
      // But we can send additional page views for SPA navigation
      if (window.gtag) {
        const pageViewEvent = {
          page_location: window.location.href,
          page_title: document.title,
          page_referrer: document.referrer,
        };
        window.gtag('event', 'page_view', pageViewEvent);
      }

      // Meta Pixel PageView (only if fbq exists and pixel was initialized in index.html)
      if (window.fbq && window.fbqPixelInitialized) {
        window.fbq('track', 'PageView');
      }

      if (params?.event === 'CompleteRegistration') {
        // Meta Pixel CompleteRegistration
        if (window.fbq && window.fbqPixelInitialized) {
          window.fbq('track', 'CompleteRegistration');
        }
      }

      // GTM PageView
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'page_view',
          page_location: window.location.href,
          page_title: document.title,
          page_referrer: document.referrer,
        });
      }
    },
  },

  schema: {
    schemas: {
      // Standard e-commerce events
      lead: z.object({
        event: z.literal('Lead'),
      }),

      purchase: z.object({
        event: z.literal('Purchase'),
        value: z.number(),
        currency: z.string(),
      }),

      // Page view schema
      page_view: z.object({
        event: z.literal('page_view'),
        page_location: z.string(),
        page_title: z.string(),
        page_referrer: z.string().optional(),
      }),
    },
    abortOnError: import.meta.env.PROD,
    onSchemaError: error => {
      // eslint-disable-next-line no-console
      console.error('Event validation error:', error);
      // Send error to monitoring in production
      if (import.meta.env.PROD && window.Sentry) {
        window.Sentry.captureException(error);
      }
    },
  },

  // Optional: Enable batching for performance
  batch: {
    enable: false, // Can be enabled for high-volume events
  },
});
